import { type ApolloClient } from "@apollo/client";
import {
  CreateEventsDocument,
  EventPayloadType,
  GetEventsDocument,
  type CreateEventsMutation,
  type CreateEventsMutationVariables,
  type EventInput,
  type GetEventsQuery,
  type GetEventsQueryVariables,
} from "@package/ui-gql/index-analytics";
import moment from "moment";
import { type Store, type UnknownAction } from "redux";
import { type ReduxWallState } from "../redux/slice-wall";
import { EventEmitter } from "../utils/event-emitter";
import { getDevice } from "../utils/wall-utils-gql";

const TIMEOUT_TRANSMIT = 1000 * 60;
const TIMEOUT_RELOAD_CHECKED = 1000 * 60 * 5;

type AnalyticsEngineEvents = {
  "cache-reload-requested": () => void;
};

export class WallEngineAnalytics extends EventEmitter<AnalyticsEngineEvents> {
  private timeoutPendingTransmit?: ReturnType<typeof setTimeout>;
  private timeoutCheckCacheReload?: ReturnType<typeof setTimeout>;
  private pendingTransmit: EventInput[] = [];
  private analyticsEnabled = false;

  constructor(
    private readonly store: Store<ReduxWallState, UnknownAction>,
    private readonly clientDataAccess: ApolloClient<object>,
    private readonly clientAnalytics?: ApolloClient<object>,
    private readonly device?: string,
  ) {
    super();
  }

  public destroy(): void {
    super.destroy();
    this.cancelTimeoutPendingTransmit();
    this.cancelTimeoutCheckCacheReload();
    this.pendingTransmit = [];
    console.log("[wall-engine-analytics] Destroyed");
  }

  public async activate(): Promise<void> {
    if (!this.clientAnalytics) {
      return;
    }

    if (this.device) {
      const device = await getDevice(this.clientDataAccess, this.device);
      if (device) {
        this.analyticsEnabled = !!device.config?.analytics;
      }
    }

    this.startTransmitEvents();
    this.startCheckCacheReload();
  }

  public enqueue(event: EventInput): void {
    console.log(`[wall-engine-analytics] Analytics Event: `, event);

    if (!this.clientAnalytics || !this.analyticsEnabled) {
      return;
    }

    this.pendingTransmit.push(event);
  }

  private cancelTimeoutPendingTransmit() {
    if (this.timeoutPendingTransmit) {
      clearTimeout(this.timeoutPendingTransmit);
      this.timeoutPendingTransmit = undefined;
      console.log("[wall-engine-analytics] Cancelled pending transmit timeout");
    }
  }

  private cancelTimeoutCheckCacheReload() {
    if (this.timeoutCheckCacheReload) {
      clearTimeout(this.timeoutCheckCacheReload);
      this.timeoutCheckCacheReload = undefined;
      console.log(
        "[wall-engine-analytics] Cancelled cache reload check timeout",
      );
    }
  }

  private startTransmitEvents() {
    this.cancelTimeoutPendingTransmit();

    this.timeoutPendingTransmit = setTimeout(async () => {
      await this.transmitEvents();
      this.startTransmitEvents();
    }, TIMEOUT_TRANSMIT);
  }

  private startCheckCacheReload(lastRequestedRangeEnd?: Date) {
    this.cancelTimeoutCheckCacheReload();

    this.timeoutCheckCacheReload = setTimeout(async () => {
      const end = await this.checkReloadCache(lastRequestedRangeEnd);
      this.startCheckCacheReload(end);
    }, TIMEOUT_RELOAD_CHECKED);
  }

  private async checkReloadCache(
    lastRequestedRangeEnd?: Date,
  ): Promise<Date | undefined> {
    if (!this.clientAnalytics) {
      return;
    }

    console.log(
      "[wall-engine-analytics] Checking for cache reload requests...",
    );

    const location = this.store.getState().wall.location;
    const start =
      lastRequestedRangeEnd ??
      moment().subtract(TIMEOUT_RELOAD_CHECKED, "milliseconds").toDate();
    const end = moment().toDate();

    try {
      const { data } = await this.clientAnalytics.query<
        GetEventsQuery,
        GetEventsQueryVariables
      >({
        query: GetEventsDocument,
        errorPolicy: "ignore",
        fetchPolicy: "network-only",
        variables: {
          filter: {
            location: location ? [location] : undefined,
            eventType: [EventPayloadType.CacheReloadRequest],
            range: {
              start,
              end,
            },
          },
          page: 0,
          perPage: 1,
          sortField: "timestamp",
          sortOrder: "DESC",
        },
      });

      if (data?.items?.length > 0) {
        console.log("[wall-engine-analytics] Cache reload Initiated");
        this.emit("cache-reload-requested");
      }
    } catch (error) {
      console.error("Error while checking cache reload", error);
    }

    console.log(
      `[wall-engine-analytics] Cache reload check completed, range: ${start} - ${end}`,
    );

    return end;
  }

  private async transmitEvents() {
    if (!this.clientAnalytics || !this.analyticsEnabled) {
      return;
    }

    console.log("[wall-engine-analytics] Transmitting pending events...");

    const state = this.store.getState();
    const events = this.pendingTransmit.slice().concat({
      type: EventPayloadType.DeviceActive,
      device: state.wall.deviceId,
      location: state.wall.location,
      profile: state.wall.profile,
      payloadOneOf: {
        DEVICE_ACTIVE: {
          initialitzation: state.wall.initalization,
        },
      },
    });

    this.pendingTransmit = [];

    try {
      await this.clientAnalytics.mutate<
        CreateEventsMutation,
        CreateEventsMutationVariables
      >({
        mutation: CreateEventsDocument,
        errorPolicy: "ignore",
        fetchPolicy: "network-only",
        variables: {
          events,
        },
      });
    } catch (error) {
      console.error("Error while sending analytics events", error);
    }

    console.log(
      `[wall-engine-analytics] Transmitted ${events.length} events successfully`,
    );
  }
}
